import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ProjectTask {
  id: string;
  project_id: string;
  title: string;
  description: string | null;
  is_complete: boolean;
  assigned_to: string[];
  due_date: string | null;
  priority: string;
  created_by: string;
  completed_at: string | null;
  created_at: string;
}

export const useProjectTasklist = (projectId: string | undefined) => {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["project_tasklist", projectId],
    queryFn: async (): Promise<ProjectTask[]> => {
      const { data, error } = await (supabase
        .from("project_tasklist") as any)
        .select("*")
        .eq("project_id", projectId!)
        .order("is_complete", { ascending: true })
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []).map((t: any) => ({
        ...t,
        assigned_to: t.assigned_to ?? [],
      }));
    },
    enabled: !!projectId,
  });

  const addTask = useMutation({
    mutationFn: async (task: { title: string; description?: string; assigned_to?: string[]; due_date?: string; priority?: string; companyId: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const { error } = await (supabase.from("project_tasklist") as any).insert({
        project_id: projectId!,
        company_id: task.companyId,
        title: task.title,
        description: task.description ?? null,
        assigned_to: task.assigned_to ?? [],
        due_date: task.due_date ?? null,
        priority: task.priority ?? "normal",
        created_by: user.id,
      });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project_tasklist", projectId] }),
  });

  const toggleTask = useMutation({
    mutationFn: async ({ taskId, isComplete }: { taskId: string; isComplete: boolean }) => {
      const { error } = await (supabase.from("project_tasklist") as any)
        .update({
          is_complete: isComplete,
          completed_at: isComplete ? new Date().toISOString() : null,
        })
        .eq("id", taskId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project_tasklist", projectId] }),
  });

  const deleteTask = useMutation({
    mutationFn: async (taskId: string) => {
      const { error } = await (supabase.from("project_tasklist") as any)
        .delete()
        .eq("id", taskId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project_tasklist", projectId] }),
  });

  const updateTask = useMutation({
    mutationFn: async ({ taskId, updates }: { taskId: string; updates: Partial<Pick<ProjectTask, "title" | "description" | "assigned_to" | "due_date" | "priority">> }) => {
      const { error } = await (supabase.from("project_tasklist") as any)
        .update(updates)
        .eq("id", taskId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project_tasklist", projectId] }),
  });

  return {
    tasks: query.data ?? [],
    isLoading: query.isLoading,
    addTask,
    toggleTask,
    deleteTask,
    updateTask,
  };
};
